import { Directive, ElementRef, OnInit, OnDestroy, Input, AfterViewInit } from '@angular/core';
import { gsap } from 'gsap';

@Directive({
  selector: '[webglGradient]',
  standalone: true
})
export class WebGLGradientDirective implements OnInit, OnDestroy, AfterViewInit {
  @Input() gradientSpeed = 0.00015;
  @Input() gradientColors = {
    color1: [15 / 255, 23 / 255, 42 / 255],   // Dark blue (slate-900)
    color2: [30 / 255, 58 / 255, 138 / 255],  // Blue-900
    color3: [37 / 255, 99 / 255, 235 / 255],  // Blue-600
    color4: [51 / 255, 65 / 255, 85 / 255]    // Slate-700
  };

  // Current animated colors as individual numeric values for GSAP (start with dark blue)
  private animatedColors = {
    color1_r: 15 / 255,
    color1_g: 23 / 255,
    color1_b: 42 / 255,
    color2_r: 30 / 255,
    color2_g: 58 / 255,
    color2_b: 138 / 255,
    color3_r: 37 / 255,
    color3_g: 99 / 255,
    color3_b: 235 / 255,
    color4_r: 51 / 255,
    color4_g: 65 / 255,
    color4_b: 85 / 255
  };

  private canvas!: HTMLCanvasElement;
  private gl!: WebGLRenderingContext;
  private program!: WebGLProgram;
  private animationId!: number;
  private startTime = Date.now();
  private resizeObserver!: ResizeObserver;
  private isHovered = false;
  private hoverIntensity = 0;

  constructor(private el: ElementRef<HTMLElement>) {}

  ngOnInit() {
    try {
      this.initCanvas();
      this.initWebGL();
      if (!this.gl) return; // WebGL unavailable — canvas already cleaned up in initWebGL
      this.animate();
      this.setupResizeObserver();
      this.setupHoverEffects();
    } catch {
      // WebGL not available — remove canvas and skip the gradient effect
      try { if (this.canvas) this.canvas.remove(); } catch { /* ignore */ }
    }
  }

  ngAfterViewInit() {
    // Colors will be animated on hover instead of continuous animation
  }

  ngOnDestroy() {
    try { if (this.animationId) cancelAnimationFrame(this.animationId); } catch { /* ignore */ }
    try { if (this.resizeObserver) this.resizeObserver.disconnect(); } catch { /* ignore */ }
    try { gsap.killTweensOf(this.animatedColors); gsap.killTweensOf(this); } catch { /* ignore */ }
  }

  private initCanvas() {
    this.canvas = document.createElement('canvas');
    this.canvas.style.position = 'absolute';
    this.canvas.style.top = '0';
    this.canvas.style.left = '0';
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.pointerEvents = 'none';
    this.canvas.style.opacity = '0.9';
    this.canvas.style.borderRadius = '4px';
    this.canvas.style.mixBlendMode = 'screen';
    this.canvas.style.zIndex = '0';
    
    const parent = this.el.nativeElement;
    const computedStyle = globalThis.getComputedStyle(parent);
    if (computedStyle.position === 'static') {
      parent.style.position = 'relative';
    }
    parent.insertBefore(this.canvas, parent.firstChild);
    this.updateCanvasSize();
  }

  private updateCanvasSize() {
    if (!this.canvas) return;
    try {
      const rect = this.el.nativeElement.getBoundingClientRect();
      const dpr = globalThis.devicePixelRatio || 1;
      this.canvas.width = rect.width * dpr || 0;
      this.canvas.height = rect.height * dpr || 0;
      if (this.gl) {
        this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
      }
    } catch { /* ignore */ }
  }

  private setupResizeObserver() {
    try {
      this.resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          try { this.updateCanvasSize(); } catch { /* ignore */ }
        });
      });
      this.resizeObserver.observe(this.el.nativeElement);
    } catch { /* ResizeObserver not supported */ }
  }

  private setupHoverEffects() {
    const element = this.el.nativeElement;

    // Define color palettes
    const darkGreenPalette = {
      color1_r: 0 / 255, color1_g: 63 / 255, color1_b: 45 / 255,     // CBRE dark green
      color2_r: 15 / 255, color2_g: 23 / 255, color2_b: 42 / 255,    // Dark blue-green mix
      color3_r: 30 / 255, color3_g: 41 / 255, color3_b: 59 / 255,    // Slate-800
      color4_r: 23 / 255, color4_g: 232 / 255, color4_b: 143 / 255   // CBRE accent green
    };

    const darkBluePalette = {
      color1_r: 15 / 255, color1_g: 23 / 255, color1_b: 42 / 255,    // Dark blue (slate-900)
      color2_r: 30 / 255, color2_g: 58 / 255, color2_b: 138 / 255,   // Blue-900
      color3_r: 37 / 255, color3_g: 99 / 255, color3_b: 235 / 255,   // Blue-600
      color4_r: 51 / 255, color4_g: 65 / 255, color4_b: 85 / 255     // Slate-700
    };

    // Mouse enter - animate to dark green
    element.addEventListener('mouseenter', () => {
      this.isHovered = true;
      try {
        // Animate hover intensity
        gsap.to(this, {
          hoverIntensity: 1,
          duration: 1.2,
          ease: 'power2.out'
        });

        // Animate colors to dark green palette
        gsap.to(this.animatedColors, {
          duration: 1.5,
          ease: 'power2.out',
          ...darkGreenPalette
        });
      } catch { /* ignore GSAP animation errors */ }
    });

    // Mouse leave - animate back to dark blue
    element.addEventListener('mouseleave', () => {
      this.isHovered = false;
      try {
        // Reset hover intensity
        gsap.to(this, {
          hoverIntensity: 0,
          duration: 1.4,
          ease: 'power2.inOut'
        });

        // Animate colors back to dark blue palette
        gsap.to(this.animatedColors, {
          duration: 1.6,
          ease: 'power2.inOut',
          ...darkBluePalette
        });
      } catch { /* ignore GSAP animation errors */ }
    });
  }

  private initWebGL() {
    if (!this.canvas) return;
    const gl = this.canvas.getContext('webgl', { 
      alpha: true, 
      antialias: true,
      premultipliedAlpha: false
    });
    
    if (!gl) {
      this.canvas.remove();
      return;
    }
    
    this.gl = gl;

    try {


    const vertexShader = this.createShader(gl.VERTEX_SHADER, `
      attribute vec2 position;
      void main() {
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `);

    const fragmentShader = this.createShader(gl.FRAGMENT_SHADER, `
      precision mediump float;
      uniform vec2 resolution;
      uniform float time;
      uniform float hoverIntensity;
      uniform vec3 color1;
      uniform vec3 color2;
      uniform vec3 color3;
      uniform vec3 color4;

      // Smooth noise function
      float noise(vec2 p) {
        return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
      }

      float smoothNoise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        f = f * f * (3.0 - 2.0 * f);
        
        float a = noise(i);
        float b = noise(i + vec2(1.0, 0.0));
        float c = noise(i + vec2(0.0, 1.0));
        float d = noise(i + vec2(1.0, 1.0));
        
        return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
      }

      void main() {
        vec2 uv = gl_FragCoord.xy / resolution;

        // Create multiple moving gradients with subtle rotation and translation
        float t = time * 0.5;

        // Subtle rotation matrix for gradient movement
        float angle = t * 0.02; // Slower rotation
        float s = sin(angle);
        float c = cos(angle);
        mat2 rotation = mat2(c, -s, s, c);

        // Apply gentle rotation and translation to UV
        vec2 centeredUV = uv - 0.5;
        vec2 rotatedUV = rotation * centeredUV + 0.5;

        // Add flowing offset that moves the gradient pattern
        vec2 flowOffset = vec2(
          sin(t * 0.08) * 0.15,
          cos(t * 0.06) * 0.15
        );

        vec2 p1 = rotatedUV + flowOffset + vec2(sin(t * 0.15) * 0.3, cos(t * 0.1) * 0.3);
        vec2 p2 = rotatedUV + flowOffset * 0.8 + vec2(cos(t * 0.2) * 0.4, sin(t * 0.15) * 0.2);
        vec2 p3 = rotatedUV + flowOffset * 0.6 + vec2(sin(t * 0.1) * 0.2, cos(t * 0.25) * 0.3);

        float n1 = smoothNoise(p1 * 2.0 + t * 0.05);
        float n2 = smoothNoise(p2 * 3.0 - t * 0.08);
        float n3 = smoothNoise(p3 * 2.5 + t * 0.04);

        // Mix colors based on noise and position
        vec3 color = mix(color1, color2, n1);
        color = mix(color, color3, n2 * rotatedUV.y);
        color = mix(color, color4, n3 * (1.0 - rotatedUV.x));

        // Add subtle radial gradient that moves with the flow
        vec2 flowCenter = vec2(0.5) + flowOffset * 0.3;
        float dist = length(uv - flowCenter);
        color = mix(color, color1 * 0.5, dist * 0.3);

        // Glass UI Effects
        // Calculate edge distances for glass highlights
        float edgeTop = smoothstep(0.0, 0.15, uv.y);
        float edgeLeft = smoothstep(0.0, 0.15, uv.x);
        float edgeBottom = smoothstep(0.0, 0.15, 1.0 - uv.y);
        float edgeRight = smoothstep(0.0, 0.15, 1.0 - uv.x);

        // Create frosted glass highlight on top-left edges
        float glassHighlight = (1.0 - edgeTop) * 0.4 + (1.0 - edgeLeft) * 0.3;
        glassHighlight = smoothstep(0.0, 0.5, glassHighlight);

        // Create subtle shadow on bottom-right edges
        float glassShadow = (1.0 - edgeBottom) * 0.2 + (1.0 - edgeRight) * 0.15;
        glassShadow = smoothstep(0.0, 0.4, glassShadow);

        // Add light transmission effect (semi-transparent white/cyan tint)
        vec3 glassColor = vec3(0.9, 0.95, 1.0); // Slight cyan tint for glass
        color = mix(color, glassColor, glassHighlight * 0.25);

        // Darken bottom-right for depth
        color *= (1.0 - glassShadow * 0.15);

        // Add specular highlight that moves across the surface
        vec2 specularPos = vec2(0.3 + sin(t * 0.1) * 0.4, 0.3 + cos(t * 0.08) * 0.4);
        float specular = 1.0 - length(uv - specularPos);
        specular = smoothstep(0.7, 0.9, specular);
        color += vec3(1.0) * specular * 0.15;

        // Add subtle iridescent shimmer at edges
        float iridescence = sin(uv.x * 10.0 + t * 0.5) * sin(uv.y * 10.0 - t * 0.5) * 0.5 + 0.5;
        float edgeProximity = min(min(uv.x, 1.0 - uv.x), min(uv.y, 1.0 - uv.y));
        float iridescentEdge = smoothstep(0.05, 0.0, edgeProximity);
        color += vec3(0.2, 0.4, 0.6) * iridescence * iridescentEdge * 0.1;

        // Enhance brightness on hover (subtle)
        float brightness = 1.0 + hoverIntensity * 0.15;
        color *= brightness;

        gl_FragColor = vec4(color, 1.0);
      }
    `);

    this.program = this.createProgram(vertexShader, fragmentShader);
    gl.useProgram(this.program);

    // Create a quad that covers the entire canvas
    const positions = new Float32Array([
      -1, -1,
       1, -1,
      -1,  1,
       1,  1,
    ]);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

    const positionLocation = gl.getAttribLocation(this.program, 'position');
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    // Initialize animated colors with gradient colors
    this.animatedColors = {
      color1_r: this.gradientColors.color1[0],
      color1_g: this.gradientColors.color1[1],
      color1_b: this.gradientColors.color1[2],
      color2_r: this.gradientColors.color2[0],
      color2_g: this.gradientColors.color2[1],
      color2_b: this.gradientColors.color2[2],
      color3_r: this.gradientColors.color3[0],
      color3_g: this.gradientColors.color3[1],
      color3_b: this.gradientColors.color3[2],
      color4_r: this.gradientColors.color4[0],
      color4_g: this.gradientColors.color4[1],
      color4_b: this.gradientColors.color4[2]
    };
    } catch {
      if (this.canvas) this.canvas.remove();
      this.gl = null!;
    }
  }

  private createShader(type: number, source: string): WebGLShader {
    const shader = this.gl.createShader(type)!;
    this.gl.shaderSource(shader, source);
    this.gl.compileShader(shader);
    
    if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {

      this.gl.deleteShader(shader);
      throw new Error('Shader compilation failed');
    }
    
    return shader;
  }

  private createProgram(vertexShader: WebGLShader, fragmentShader: WebGLShader): WebGLProgram {
    const program = this.gl.createProgram()!;
    this.gl.attachShader(program, vertexShader);
    this.gl.attachShader(program, fragmentShader);
    this.gl.linkProgram(program);
    
    if (!this.gl.getProgramParameter(program, this.gl.LINK_STATUS)) {
      this.gl.deleteProgram(program);
      throw new Error('Program linking failed');
    }
    
    return program;
  }

  private animate = () => {
    if (!this.gl || !this.program) return;
    this.animationId = requestAnimationFrame(this.animate);
    try {
      const currentTime = Date.now() - this.startTime;
    // Speed up animation on hover (reduced multiplier)
    const speedMultiplier = 1 + this.hoverIntensity * 0.8;
    const time = currentTime * this.gradientSpeed * speedMultiplier;

    this.gl.useProgram(this.program);

    const resolutionLocation = this.gl.getUniformLocation(this.program, 'resolution');
    const timeLocation = this.gl.getUniformLocation(this.program, 'time');
    const hoverIntensityLoc = this.gl.getUniformLocation(this.program, 'hoverIntensity');
    const color1Loc = this.gl.getUniformLocation(this.program, 'color1');
    const color2Loc = this.gl.getUniformLocation(this.program, 'color2');
    const color3Loc = this.gl.getUniformLocation(this.program, 'color3');
    const color4Loc = this.gl.getUniformLocation(this.program, 'color4');

    this.gl.uniform2f(resolutionLocation, this.canvas.width, this.canvas.height);
    this.gl.uniform1f(timeLocation, time);
    this.gl.uniform1f(hoverIntensityLoc, this.hoverIntensity);

    // Construct color arrays from GSAP-animated individual channel values
    const color1 = new Float32Array([
      this.animatedColors.color1_r,
      this.animatedColors.color1_g,
      this.animatedColors.color1_b
    ]);
    const color2 = new Float32Array([
      this.animatedColors.color2_r,
      this.animatedColors.color2_g,
      this.animatedColors.color2_b
    ]);
    const color3 = new Float32Array([
      this.animatedColors.color3_r,
      this.animatedColors.color3_g,
      this.animatedColors.color3_b
    ]);
    const color4 = new Float32Array([
      this.animatedColors.color4_r,
      this.animatedColors.color4_g,
      this.animatedColors.color4_b
    ]);

    this.gl.uniform3fv(color1Loc, color1);
    this.gl.uniform3fv(color2Loc, color2);
    this.gl.uniform3fv(color3Loc, color3);
    this.gl.uniform3fv(color4Loc, color4);

    this.gl.clearColor(0, 0, 0, 0);
    this.gl.clear(this.gl.COLOR_BUFFER_BIT);
    this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);
    } catch { /* ignore render errors */ }
  };
}
